import React from "react";
import { useAuth } from "@/context/AuthContext";
import { AntreanUnggah } from "@/lib/antreanUnggah";
import { simpanUnggah } from "@/lib/simpanUnggah";
import { unggahFileMaster, kirimBagianXhr, sidikFile } from "@/lib/unggahFileMaster";
import { apiFileMasterUnggah, apiFileMasterBatal, formatApiErrorDetail } from "@/lib/api";
import PanelUnggahan from "@/components/PanelUnggahan";

// v2.22.2 (owner 2026-10-30, docs/rencana/UNGGAH-BANYAK.md): the file master upload queue lives here, above the
// pages, so changing menu never stops it (like Google Drive on the web). One queue per tab and per account.
const Ctx = React.createContext(null);
const PENGUNGGAH = ["admin", "desain", "pattern_layout"]; // the server checks again on every request
const KOSONG = [];
const tanpaDengar = () => () => {};

// every tab holds a Web Lock while it is open: a queue kept by a tab that is still open is never offered to
// another tab, so one file is not uploaded by two tabs at once
const TAB = `t${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
const AWALAN_KUNCI = "soqprint-unggah-tab:";
if (typeof navigator !== "undefined" && navigator.locks?.request) {
  navigator.locks.request(AWALAN_KUNCI + TAB, () => new Promise(() => {})).catch(() => {});
}
async function tabHidup() {
  try {
    const q = await navigator.locks.query();
    return new Set((q.held || []).map((l) => l.name).filter((n) => n?.startsWith(AWALAN_KUNCI)).map((n) => n.slice(AWALAN_KUNCI.length)));
  } catch { return new Set(); } // no Web Locks (older browser): every stored row is offered
}

const teksGalat = (e) => (e?.response ? formatApiErrorDetail(e.response?.data?.detail) : e?.message) || "Terjadi kesalahan";

export function UnggahanProvider({ children }) {
  const { user } = useAuth() || {};
  const uid = user && user.id;
  const pengunggah = !!user && PENGUNGGAH.includes(user.role);
  const antrean = React.useMemo(() => (uid ? new AntreanUnggah({
    userId: uid, tabId: TAB, simpan: simpanUnggah, sidik: sidikFile, teksGalat,
    unggah: (o) => unggahFileMaster({ ...o, api: apiFileMasterUnggah, kirimBagian: kirimBagianXhr }),
    batalServer: (fid) => apiFileMasterBatal(fid),
  }) : null), [uid]);
  React.useEffect(() => () => antrean?.hentikan(), [antrean]); // logout / another account

  React.useEffect(() => { // after the browser was closed: offer what is left of this account
    if (!antrean || !pengunggah) return;
    let batal = false;
    (async () => {
      const recs = await simpanUnggah.semua();
      const hidup = await tabHidup();
      hidup.delete(TAB);
      if (!batal) antrean.pulihkan(recs, hidup);
    })();
    return () => { batal = true; };
  }, [antrean, pengunggah]);

  const daftar = React.useSyncExternalStore(antrean ? antrean.dengar.bind(antrean) : tanpaDengar,
    () => (antrean ? antrean.daftar() : KOSONG));
  const belum = !!antrean && daftar.length > 0 && antrean.belumSelesai();
  React.useEffect(() => { // closing the tab/browser with the queue unfinished asks first
    if (!belum) return undefined;
    const tahan = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", tahan);
    return () => window.removeEventListener("beforeunload", tahan);
  }, [belum]);

  const nilai = React.useMemo(() => ({ antrean, daftar }), [antrean, daftar]);
  return (
    <Ctx.Provider value={nilai}>
      {children}
      {antrean && daftar.length > 0 && <PanelUnggahan antrean={antrean} daftar={daftar} />}
    </Ctx.Provider>
  );
}

export const useUnggahan = () => React.useContext(Ctx);
